/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const faIR: LocalizationResource = {
  locale: 'fa-IR',
  apiKeys: {
    action__add: 'افزودن کلید جدید',
    action__search: 'جستجوی کلیدها',
    copySecret: {
      formButtonPrimary__copyAndClose: 'کپی و بستن',
      formHint: 'به دلایل امنیتی، ما بعداً به شما اجازه مشاهده دوباره آن را نخواهیم داد.',
      formTitle: 'اکنون کلید API "{{name}}" خود را کپی کنید',
    },
    createdAndExpirationStatus__expiresOn:
      "ایجاد شده {{ createdDate | shortDate('en-US') }} • منقضی می‌شود {{ expiresDate | longDate('en-US') }}",
    createdAndExpirationStatus__never: "ایجاد شده {{ createdDate | shortDate('en-US') }} • هرگز منقضی نمی‌شود",
    detailsTitle__emptyRow: 'هیچ کلید API یافت نشد',
    formButtonPrimary__add: 'ایجاد کلید',
    formFieldCaption__expiration__expiresOn: 'منقضی می‌شود {{ date }}',
    formFieldCaption__expiration__never: 'این کلید هرگز منقضی نخواهد شد',
    formFieldOption__expiration__180d: '۱۸۰ روز',
    formFieldOption__expiration__1d: '۱ روز',
    formFieldOption__expiration__1y: '۱ سال',
    formFieldOption__expiration__30d: '۳۰ روز',
    formFieldOption__expiration__60d: '۶۰ روز',
    formFieldOption__expiration__7d: '۷ روز',
    formFieldOption__expiration__90d: '۹۰ روز',
    formFieldOption__expiration__never: 'هرگز',
    formHint: 'نامی برای تولید کلید جدید ارائه دهید. در هر زمان می‌توانید آن را لغو کنید.',
    formTitle: 'افزودن کلید API جدید',
    lastUsed__days: '{{days}} روز پیش',
    lastUsed__hours: '{{hours}} ساعت پیش',
    lastUsed__minutes: '{{minutes}} دقیقه پیش',
    lastUsed__months: '{{months}} ماه پیش',
    lastUsed__seconds: '{{seconds}} ثانیه پیش',
    lastUsed__years: '{{years}} سال پیش',
    menuAction__revoke: 'لغو کلید',
    revokeConfirmation: {
      confirmationText: 'لغو',
      formButtonPrimary__revoke: 'لغو کلید',
      formHint: 'آیا مطمئن هستید که می‌خواهید این کلید محرمانه را حذف کنید؟',
      formTitle: 'کلید محرمانه "{{apiKeyName}}" لغو شود؟',
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: 'بازگشت',
  badge__activePlan: 'فعال',
  badge__banned: undefined,
  badge__canceledEndsAt: "لغو شد • پایان می‌یابد {{ date | shortDate('en-US') }}",
  badge__currentPlan: 'طرح فعلی',
  badge__default: 'پیش فرض',
  badge__endsAt: "به پایان می رسد {{ date | shortDate('en-US') }}",
  badge__expired: 'مننقضی شده',
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'دستگاه جعل هویت دیگر',
  badge__pastDueAt: "سررسید گذشته {{ date | shortDate('en-US') }}",
  badge__pastDuePlan: 'سررسید گذشته',
  badge__primary: 'اولیه',
  badge__renewsAt: "{{ date | shortDate('en-US') }} را تمدید می‌کند",
  badge__requiresAction: 'نیاز به اقدام دارد',
  badge__startsAt: "شروع می‌شود {{ date | shortDate('en-US') }}",
  badge__thisDevice: 'این دستگاه',
  badge__trialEndsAt: "آزمایشی پایان می‌یابد {{ date | shortDate('en-US') }}",
  badge__unverified: 'تایید نشده',
  badge__upcomingPlan: 'به زودی',
  badge__userDevice: 'دستگاه کاربر',
  badge__you: 'شما',
  billing: {
    accountCredit: undefined,
    addPaymentMethod__label: 'افزودن روش پرداخت',
    alwaysFree: 'همیشه رایگان',
    annually: 'سالانه',
    availableFeatures: 'ویژگی ها',
    billedAnnually: 'صورتحساب سالانه',
    billedAnnuallyOnly: undefined,
    billedMonthly: undefined,
    billedMonthlyOnly: 'صورتحساب هر ماه',
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: 'لغو اشتراک',
    cancelSubscriptionAccessUntil:
      "شما می‌توانید تا تاریخ {{ date | longDate('en-US') }} از ویژگی‌های '{{plan}}' استفاده کنید، پس از آن دیگر دسترسی نخواهید داشت.",
    cancelSubscriptionNoCharge: 'برای این اشتراک هیچ هزینه‌ای از شما دریافت نخواهد شد.',
    cancelSubscriptionPastDue: undefined,
    cancelSubscriptionTitle: 'اشتراک {{plan}} لغو شود؟',
    cannotSubscribeMonthly:
      'شما نمی‌توانید با پرداخت ماهانه در این طرح مشترک شوید. برای عضویت در این طرح، باید پرداخت سالانه را انتخاب کنید.',
    cannotSubscribeUnrecoverable: 'شما نمی‌توانید در این طرح مشترک شوید. اشتراک موجود شما گران‌تر از این طرح است.',
    checkout: {
      description__paymentSuccessful: 'پرداخت شما با موفقیت انجام شد.',
      description__subscriptionSuccessful: 'اشتراک شما با موفقیت ایجاد شد.',
      downgradeNotice:
        'شما اشتراک فعلی و ویژگی‌های آن را تا پایان دوره صورتحساب حفظ خواهید کرد، سپس به این اشتراک منتقل خواهید شد.',
      emailForm: {
        subtitle:
          'قبل از اینکه بتوانید خرید خود را تکمیل کنید، باید یک آدرس ایمیل اضافه کنید که رسیدها به آن ارسال شوند.',
        title: 'یک آدرس ایمیل اضافه کنید',
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: 'روش پرداخت',
        title__statementId: 'شناسه اظهارنامه',
        title__subscriptionBegins: 'شروع اشتراک',
        title__totalPaid: 'کل پرداختی',
      },
      pastDueNotice: 'اشتراک قبلی شما سررسید گذشته بود، بدون پرداخت.',
      perMonth: 'ماهانه',
      title: 'تسویه حساب',
      title__paymentSuccessful: 'پرداخت موفقیت آمیز بود!',
      title__subscriptionSuccessful: 'موفقیت آمیز!',
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
      totalDuePerPeriod: undefined,
    },
    credit: 'اعتبار',
    creditRemainder: 'اعتبار برای باقیمانده اشتراک فعلی شما.',
    defaultFreePlanActive: 'شما در حال حاضر در طرح رایگان هستید',
    free: 'رایگان',
    getStarted: 'شروع کنید',
    highlightedPlanBadge: 'محبوب',
    keepFreeTrial: undefined,
    keepSubscription: 'حفظ اشتراک',
    manage: 'مدیریت',
    manageSubscription: 'مدیریت اشتراک',
    month: 'ماه',
    monthAbbreviation: undefined,
    monthPerUnit: undefined,
    monthly: 'ماهانه',
    pastDue: 'سررسید گذشته',
    pay: 'پرداخت {{amount}}',
    payerCreditRemainder: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: 'پرداخت سالانه',
        monthly: 'پرداخت ماهانه',
      },
      dev: {
        anyNumbers: 'هر عددی',
        cardNumber: 'شماره کارت',
        cvcZip: 'CVC, ZIP',
        developmentMode: 'حالت توسعه',
        expirationDate: 'تاریخ انقضا',
        testCardInfo: 'تست اطلاعات کارت',
      },
    },
    paymentMethods__label: 'روش های پرداخت',
    pricingTable: {
      billingCycle: 'چرخه صورتحساب',
      included: 'شامل',
      seatCost: {
        additionalSeats: undefined,
        freeUpToSeats: undefined,
        includedSeats: undefined,
        perSeat: undefined,
        tooltip: {
          additionalSeatsEach: undefined,
          firstSeatsIncludedInPlan: undefined,
          freeForUpToSeats: undefined,
        },
        unlimitedSeats: undefined,
        upToSeats: undefined,
      },
    },
    proratedDiscount: undefined,
    prorationCredit: undefined,
    reSubscribe: 'اشتراک مجدد',
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: undefined,
    seatsWithLimit: undefined,
    seeAllFeatures: 'مشاهده همه ویژگی‌ها',
    startFreeTrial: undefined,
    startFreeTrial__days: 'شروع آزمایش رایگان {{days}} روزه',
    subscribe: 'مشترک شوید',
    subscriptionDetails: {
      beginsOn: 'شروع در',
      currentBillingCycle: 'دوره صورتحساب فعلی',
      endsOn: 'پایان در',
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: 'مبلغ پرداخت بعدی',
      nextPaymentOn: 'پرداخت بعدی در',
      pastDueAt: 'سررسید گذشته در',
      renewsAt: 'تمدید در',
      subscribedOn: 'مشترک شده در',
      title: 'اشتراک',
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: 'جمع فرعی',
    subtotalRenewal: undefined,
    switchPlan: 'به این طرح تغییر دهید',
    switchToAnnual: 'به سالانه تغییر دهید',
    switchToAnnualWithAnnualPrice: 'تغییر به سالانه {{price}} / سال',
    switchToMonthly: 'به ماهانه تغییر دهید',
    switchToMonthlyWithPrice: 'تغییر به ماهانه {{price}} / ماه',
    totalDue: 'کل مبلغ سررسید',
    totalDuePerPeriod: undefined,
    totalDueToday: 'سررسید کل امروز',
    viewFeatures: 'مشاهده ویژگی ها',
    viewPayment: 'مشاهده پرداخت',
    year: 'سال',
    yearAbbreviation: undefined,
    yearPerUnit: undefined,
  },
  configureSSO: {
    activate: {
      activateButton: undefined,
      activeSubtitle: undefined,
      activeTitle: undefined,
      doneButton: undefined,
      skipButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    changeProviderDialog: {
      cancelButton: undefined,
      confirmButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    configureStep: {
      activeConnectionWarning: {
        dismiss: undefined,
        title: undefined,
      },
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userAttribute: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              firstName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              lastName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            copyClaimName: undefined,
            copyClaimNameCopied: undefined,
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          step1: undefined,
          step2: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'برای ارتقای مجوزهای خود با مدیر سازمان تماس بگیرید.',
      title: 'شما اجازه مدیریت ورود یکپارچه (SSO) را ندارید',
    },
    navbar: {
      title: 'پیکربندی ورود یکپارچه (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__expired: undefined,
        badge__unverified: 'تأییدنشده',
        badge__verified: 'تأییدشده',
        expiredAtLabel: undefined,
        expiredLabel: undefined,
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: 'میزبان / نام',
          instructions:
            'این رکورد TXT را به ارائه‌دهنده DNS خود اضافه کنید. به‌محض فعال شدن رکورد، به‌طور خودکار آن را تأیید می‌کنیم.',
          typeLabel: 'نوع',
          valueLabel: 'مقدار',
        },
        verifiedAtLabel: "تأیید‌شده در {{ date | shortDate('fa-IR') }}",
        verifyAgainButton: undefined,
      },
      domainSuggestion: {
        formButtonPrimary__add: 'افزودن {{domain}}',
        messageLabel: 'ایمیل شما از {{domain}} استفاده می‌کند. آیا می‌خواهید آن را اضافه کنید؟',
      },
      formButtonPrimary__add: 'افزودن',
      formFieldInputPlaceholder__domain: 'افزودن دامنه',
      formFieldLabel__domain: 'دامنه',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle: 'مالکیت دامنه‌هایی را که سازمان شما برای ورود استفاده می‌کند، اضافه و تأیید کنید.',
      title: 'افزودن دامنه‌های SSO',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: 'ارائه‌دهنده SAML سفارشی',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: 'ارائه‌دهنده‌ای را که می‌خواهید SSO را برای آن راه‌اندازی کنید، انتخاب کنید.',
      title: 'ارائه‌دهنده را انتخاب کنید',
      warning: 'پس از انتخاب یک ارائه‌دهنده، نمی‌توانید آن را تا پایان پیکربندی تغییر دهید',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: 'ایجاد سازمان',
    invitePage: {
      formButtonReset: 'گذشتن',
    },
    title: 'ایجاد سازمان',
  },
  dates: {
    lastDay: "دیروز در {{ date | timeString('en-US') }}",
    next6Days: "{{ date | weekday('en-US','long') }} at {{ date | timeString('en-US') }}",
    nextDay: "فردا در {{ date | timeString('en-US') }}",
    numeric: "{{ date | numeric('en-US') }}",
    previous6Days: "آخرین {{ date | weekday('en-US','long') }} در {{ date | timeString('en-US') }}",
    sameDay: "امروز در {{ date | timeString('en-US') }}",
  },
  dividerText: 'یا',
  footerActionLink__alternativePhoneCodeProvider: 'به جای آن، کد را از طریق پیامک ارسال کنید',
  footerActionLink__useAnotherMethod: 'استفاده از روش دیگر',
  footerPageLink__help: 'کمک',
  footerPageLink__privacy: 'حریم خصوصی',
  footerPageLink__terms: 'شرایط',
  formButtonPrimary: 'ادامه',
  formButtonPrimary__verify: 'تأیید',
  formFieldAction__forgotPassword: 'رمز عبور را فراموش کرده‌اید؟',
  formFieldError__matchingPasswords: 'رمزهای عبور مطابقت دارند.',
  formFieldError__notMatchingPasswords: 'رمزهای عبور مطابقت ندارند.',
  formFieldError__verificationLinkExpired: 'لینک تأیید منقضی شده است. لطفاً درخواست لینک جدید بدهید.',
  formFieldHintText__optional: 'اختیاری',
  formFieldHintText__slug:
    'اسلاگ یک شناسه قابل خواندن توسط انسان است که باید منحصر به فرد باشد. اغلب در URL ها استفاده می شود.',
  formFieldInputPlaceholder__apiKeyDescription: 'توضیح کوتاهی از کاربرد این کلید وارد کنید',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'تاریخ انقضا را انتخاب کنید',
  formFieldInputPlaceholder__apiKeyName: 'نام کلید API را وارد کنید',
  formFieldInputPlaceholder__backupCode: 'کد پشتیبان را وارد کنید',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'حذف حساب',
  formFieldInputPlaceholder__emailAddress: 'آدرس ایمیل خود را وارد کنید',
  formFieldInputPlaceholder__emailAddress_username: 'ایمیل یا نام کاربری را وارد کنید',
  formFieldInputPlaceholder__emailAddresses: 'example@email.com, example2@email.com',
  formFieldInputPlaceholder__firstName: 'نام',
  formFieldInputPlaceholder__lastName: 'نام خانوادگی',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'you@example.com',
  formFieldInputPlaceholder__organizationName: 'نام سازمان',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: 'رمز عبور خود را وارد کنید',
  formFieldInputPlaceholder__phoneNumber: 'شماره تلفن خود را وارد کنید',
  formFieldInputPlaceholder__signUpPassword: undefined,
  formFieldInputPlaceholder__username: 'نام کاربری خود را وارد کنید',
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'کلید API',
  formFieldLabel__apiKeyDescription: 'توضیحات کلید API',
  formFieldLabel__apiKeyExpiration: 'انقضای کلید API',
  formFieldLabel__apiKeyName: 'نام کلید API',
  formFieldLabel__automaticInvitations: 'دعوت‌های خودکار را برای این دامنه فعال کنید',
  formFieldLabel__backupCode: 'کد  پشتیبان',
  formFieldLabel__confirmDeletion: 'تایید',
  formFieldLabel__confirmPassword: 'رمز عبور را تایید کنید',
  formFieldLabel__currentPassword: 'رمز عبور فعلی',
  formFieldLabel__emailAddress: 'آدرس ایمیل',
  formFieldLabel__emailAddress_username: 'آدرس ایمیل یا نام کاربری',
  formFieldLabel__emailAddresses: 'آدرس های ایمیل',
  formFieldLabel__firstName: 'نام',
  formFieldLabel__lastName: 'نام خانوادگی',
  formFieldLabel__newPassword: 'رمز عبور جدید',
  formFieldLabel__organizationDomain: 'دامنه',
  formFieldLabel__organizationDomainDeletePending: 'حذف دعوت‌نامه‌ها و پیشنهادهای در انتظار',
  formFieldLabel__organizationDomainEmailAddress: 'آدرس ایمیل تایید',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'برای دریافت کد و تأیید این دامنه، یک آدرس ایمیل در زیر این دامنه وارد کنید.',
  formFieldLabel__organizationName: 'نام سازمان',
  formFieldLabel__organizationSlug: 'اسلاگ',
  formFieldLabel__passkeyName: 'نام کلید عبور',
  formFieldLabel__password: 'رمز عبور',
  formFieldLabel__phoneNumber: 'شماره تلفن',
  formFieldLabel__role: 'نقش',
  formFieldLabel__signOutOfOtherSessions: 'از همه دستگاه‌های دیگر خارج شوید',
  formFieldLabel__username: 'نام کاربری',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: 'خروج',
    title: 'با نام کاربری {{identifier}} وارد سیستم شدم',
  },
  lastAuthenticationStrategy: 'آخرین استفاده',
  maintenanceMode: 'در حال حاضر در حال تعمیر و نگهداری هستیم، اما نگران نباشید، نباید بیشتر از چند دقیقه طول بکشد.',
  membershipRole__admin: 'مدیر',
  membershipRole__basicMember: 'عضو',
  membershipRole__guestMember: 'مهمان',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: 'ایجاد سازمان',
    action__invitationAccept: 'بپیوندید',
    action__suggestionsAccept: 'درخواست عضویت',
    createOrganization: 'ایجاد سازمان',
    invitationAcceptedLabel: 'عضو شدید',
    subtitle: 'برای ادامه به {{applicationName}}',
    suggestionsAcceptedLabel: 'در انتظار تایید',
    title: 'یک حساب انتخاب کنید',
    titleWithoutPersonal: 'یک سازمان را انتخاب کنید',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'کلیدهای API',
    },
    badge__automaticInvitation: 'دعوت نامه های خودکار',
    badge__automaticSuggestion: 'پیشنهادات خودکار',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: 'بدون ثبت نام خودکار',
    badge__unverified: 'تایید نشده',
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'تاریخچه پرداخت خالی است',
        notFound: 'هیچ پرداختی یافت نشد',
        tableHeader__amount: 'مبلغ',
        tableHeader__date: 'تاریخ',
        tableHeader__status: 'وضعیت',
      },
      paymentMethodsSection: {
        actionLabel__default: 'پیش فرض کنید',
        actionLabel__remove: 'حذف',
        add: 'افزودن روش پرداخت جدید',
        addSubtitle: 'یک روش پرداخت جدید به حساب خود اضافه کنید.',
        cancelButton: 'لغو',
        formButtonPrimary__add: 'افزودن روش پرداخت',
        formButtonPrimary__pay: '{{amount}} پرداخت',
        payWithTestCardButton: 'پرداخت با کارت تست',
        removeMethod: {
          messageLine1: '{{identifier}} از این حساب حذف خواهد شد.',
          messageLine2:
            'شما دیگر نمی‌توانید از این منبع پرداخت استفاده کنید و هرگونه اشتراک دوره‌ای وابسته به آن دیگر کار نخواهد کرد.',
          successMessage: '{{paymentMethod}} از حساب شما حذف شده است.',
          title: 'حذف روش پرداخت',
        },
        title: 'روش های پرداخت',
      },
      start: {
        headerTitle__payments: 'پرداخت‌ها',
        headerTitle__plans: 'طرح ها',
        headerTitle__statements: 'بیانیه‌ها',
        headerTitle__subscriptions: 'اشتراک ها',
      },
      statementsSection: {
        empty: 'بیانیه‌ها خالی است',
        itemCaption__paidForPlan: 'پرداخت شده برای طرح {{plan}}',
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: 'اعتبار تناسبی',
        itemCaption__subscribedAndPaidForPlan: 'مشترک و پرداخت شده برای طرح {{plan}}',
        notFound: 'هیچ بیانیه‌ای یافت نشد',
        tableHeader__amount: 'مبلغ',
        tableHeader__date: 'تاریخ',
        title: 'بیانیه‌ها',
        totalPaid: 'کل پرداختی',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: 'در یک طرح مشترک شوید',
        actionLabel__switchPlan: 'تغییر طرح',
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: 'اشتراک',
      },
      subscriptionsSection: {
        actionLabel__default: 'مدیریت',
      },
      switchPlansSection: {
        title: 'تغییر طرح',
      },
      title: 'صورتحساب',
    },
    createDomainPage: {
      subtitle:
        'دامنه را برای تأیید اضافه کنید. کاربرانی که آدرس ایمیل آنها در این دامنه است می‌توانند به‌طور خودکار به سازمان بپیوندند یا درخواست عضویت بدهند.',
      title: 'افزودن دامنه',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'دعوت‌نامه‌ها ارسال نشدند. در حال حاضر دعوت‌نامه‌هایی برای آدرس‌های ایمیل زیر در انتظار ارسال هستند: {{email_addresses}}.',
      formButtonPrimary__continue: 'ارسال دعوت‌ نامه‌ها',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: 'انتخاب نقش',
      subtitle: 'یک یا چند آدرس ایمیل را وارد یا جای‌گذاری کنید، که با فاصله یا ویرگول از هم جدا شده‌اند.',
      successMessage: 'دعوتنامه‌ها با موفقیت ارسال شدند',
      title: 'دعوت از اعضای جدید',
    },
    membersPage: {
      action__invite: 'دعوت',
      action__search: 'جستجو',
      activeMembersTab: {
        menuAction__remove: 'حذف عضو',
        tableHeader__actions: 'عملیات',
        tableHeader__joined: 'عضو شدید',
        tableHeader__role: 'نقش',
        tableHeader__user: 'کاربر',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle:
            'ما در حال به‌روزرسانی نقش‌های موجود هستیم. پس از اتمام، می‌توانید دوباره نقش‌ها را به‌روزرسانی کنید.',
          title: 'نقش‌ها موقتاً قفل شده‌اند',
        },
      },
      detailsTitle__emptyRow: 'هیچ عضوی برای نمایش وجود ندارد',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'با اتصال یک دامنه ایمیل به سازمان خود، کاربران را دعوت کنید. هر کسی که با یک دامنه ایمیل منطبق ثبت نام کند، می‌تواند در هر زمان به سازمان بپیوندد.',
          headerTitle: 'دعوت نامه های خودکار',
          primaryButton: 'دامنه های تایید شده را مدیریت کنید',
        },
        table__emptyRow: 'دعوتنامه‌ای برای نمایش وجود ندارد',
      },
      invitedMembersTab: {
        menuAction__revoke: 'ابطال دعوت',
        tableHeader__invited: 'دعوت شده',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'کاربرانی که با دامنه ایمیل منطبق ثبت نام می‌کنند، می‌توانند پیشنهادی برای درخواست عضویت در سازمان شما را مشاهده کنند.',
          headerTitle: 'پیشنهادات خودکار',
          primaryButton: 'دامنه های تایید شده را مدیریت کنید',
        },
        menuAction__approve: 'تایید',
        menuAction__reject: 'رد',
        tableHeader__requested: 'درخواست دسترسی کرد',
        table__emptyRow: 'هیچ درخواستی برای نمایش وجود ندارد',
      },
      start: {
        headerTitle__invitations: 'دعوت نامه ها',
        headerTitle__members: 'اعضا',
        headerTitle__requests: 'درخواست‌ها',
      },
    },
    navbar: {
      apiKeys: 'کلیدهای API',
      billing: 'صورتحساب',
      description: 'سازمان خود را مدیریت کنید',
      general: 'عمومی',
      members: 'اعضا',
      security: undefined,
      title: 'سازمان',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'شما مجوز مدیریت صورتحساب این سازمان را ندارید.',
        planMembershipLimitExceeded: undefined,
      },
      title: 'طرح ها',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'برای ادامه، عبارت "{{organizationName}}" را در زیر تایپ کنید.',
          messageLine1: 'آیا از حذف این سازمان مطمئن هستید؟',
          messageLine2: 'این اقدام دائمی و غیرقابل برگشت است.',
          successMessage: 'شما سازمان را حذف کرده‌اید.',
          title: 'حذف سازمان',
        },
        leaveOrganization: {
          actionDescription: 'برای ادامه، عبارت "{{organizationName}}" را در زیر تایپ کنید.',
          messageLine1:
            'آیا مطمئن هستید که می‌خواهید این سازمان را ترک کنید؟ شما دسترسی به این سازمان و برنامه‌های آن را از دست خواهید داد.',
          messageLine2: 'این اقدام دائمی و غیرقابل برگشت است.',
          successMessage: 'شما از سازمان جدا شده‌اید.',
          title: 'ترک سازمان',
        },
        title: 'خطر',
      },
      domainSection: {
        menuAction__manage: 'مدیریت',
        menuAction__remove: 'حذف',
        menuAction__verify: 'تأیید',
        primaryButton: 'افزودن دامنه',
        subtitle:
          'به کاربران اجازه دهید به‌طور خودکار به سازمان بپیوندند یا بر اساس یک دامنه ایمیل تأیید شده، درخواست عضویت دهند.',
        title: 'دامنه های تایید شده',
      },
      successMessage: 'سازمان به‌روزرسانی شده است.',
      title: 'به روز رسانی نمایه',
    },
    removeDomainPage: {
      messageLine1: 'دامنه ایمیل {{domain}} حذف خواهد شد.',
      messageLine2: 'کاربران پس از این نمی‌توانند به‌طور خودکار به سازمان بپیوندند.',
      successMessage: '{{domain}} حذف شده است.',
      title: 'حذف دامنه',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
        tooltip: undefined,
        tooltipLabel: undefined,
        tooltip__noRole: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: 'عمومی',
      headerTitle__members: 'اعضا',
      membershipSeatUsageLabel: undefined,
      profileSection: {
        primaryButton: 'به روز رسانی نمایه',
        title: 'نمایه سازمان',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'حذف این دامنه بر کاربران دعوت‌شده تأثیر خواهد گذاشت.',
        removeDomainActionLabel__remove: 'حذف دامنه',
        removeDomainSubtitle: 'حذف این دامنه از دامنه های تایید شده',
        removeDomainTitle: 'حذف دامنه',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'کاربران هنگام ثبت نام به طور خودکار برای پیوستن به سازمان دعوت می‌شوند و می‌توانند هر زمان که بخواهند به آن بپیوندند.',
        automaticInvitationOption__label: 'دعوت نامه های خودکار',
        automaticSuggestionOption__description:
          'کاربران پیشنهادی برای درخواست عضویت دریافت می‌کنند، اما قبل از اینکه بتوانند به سازمان بپیوندند، باید توسط مدیر تأیید شوند.',
        automaticSuggestionOption__label: 'پیشنهادات خودکار',
        calloutInfoLabel: 'تغییر حالت ثبت نام فقط روی کاربران جدید تأثیر می‌گذارد..',
        calloutInvitationCountLabel: 'دعوت ‌نامه‌های ارسال‌شده به کاربران در انتظار: {{count}}',
        calloutSuggestionCountLabel: 'پیشنهادات در انتظار ارسال به کاربران: {{count}}',
        manualInvitationOption__description: 'کاربران فقط می‌توانند به صورت دستی به سازمان دعوت شوند.',
        manualInvitationOption__label: 'بدون ثبت نام خودکار',
        subtitle: 'نحوه‌ی عضویت کاربران از این دامنه در سازمان را انتخاب کنید.',
      },
      start: {
        headerTitle__danger: 'خطر',
        headerTitle__enrollment: 'گزینه های ثبت نام',
      },
      subtitle: 'دامنه {{domain}} اکنون تأیید شده است. با انتخاب حالت ثبت نام، ادامه دهید..',
      title: 'به‌روزرسانی {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'کد تایید ارسال شده به آدرس ایمیل خود را وارد کنید',
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'دامنه {{domainName}} باید از طریق ایمیل تأیید شود.',
      subtitleVerificationCodeScreen: 'کد تایید به {{emailAddress}} ارسال شد. برای ادامه، کد را وارد کنید.',
      title: 'تأیید دامنه',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'ایجاد سازمان',
    action__invitationAccept: 'بپیوندید',
    action__manageOrganization: 'مدیریت',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'درخواست عضویت',
    notSelected: 'هیچ سازمانی انتخاب نشده است',
    personalWorkspace: 'حساب شخصی',
    suggestionsAcceptedLabel: 'در انتظار تایید',
  },
  paginationButton__next: 'بعدی',
  paginationButton__previous: 'قبلی',
  paginationRowText__displaying: 'در حال نمایش',
  paginationRowText__of: 'از',
  reverification: {
    alternativeMethods: {
      actionLink: 'کمک بگیرید',
      actionText: 'هیچ کدام از اینها را ندارید؟',
      blockButton__backupCode: 'از کد پشتیبان استفاده کنید',
      blockButton__emailCode: 'کد را به {{identifier}} ایمیل کنید',
      blockButton__passkey: 'از کلید عبور خود استفاده کنید',
      blockButton__password: 'با رمز عبور خود ادامه دهید',
      blockButton__phoneCode: 'ارسال کد پیامکی به {{identifier}}',
      blockButton__totp: 'از برنامه تأیید هویت خود استفاده کنید',
      getHelp: {
        blockButton__emailSupport: 'پشتیبانی ایمیل',
        content:
          'اگر در تأیید حساب کاربری خود مشکل دارید، به ما ایمیل بزنید و ما در اسرع وقت به شما کمک میکنیم تا دسترسی دوباره داشته باشید.',
        title: 'کمک بگیرید',
      },
      subtitle: 'با مشکلاتی مواجه شدید؟ می‌توانید از هر یک از این روش‌ها برای تأیید استفاده کنید.',
      title: 'استفاده از روش دیگر',
    },
    backupCodeMfa: {
      subtitle: 'کد پشتیبان دریافتی هنگام تنظیم احراز هویت دو مرحله‌ای را وارد کنید',
      title: 'یک کد پشتیبان وارد کنید',
    },
    emailCode: {
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه، کد ارسال شده به ایمیل خود را وارد کنید',
      title: 'تایید هویت الزامی است',
    },
    noAvailableMethods: {
      message: 'امکان ادامه تأیید وجود ندارد. هیچ عامل احراز هویت مناسبی پیکربندی نشده است.',
      subtitle: 'خطایی رخ داد',
      title: 'حساب شما قابل تأیید نیست',
    },
    passkey: {
      blockButton__passkey: 'از کلید عبور خود استفاده کنید',
      subtitle:
        'استفاده از کلید عبور، هویت شما را تأیید می‌کند. ممکن است دستگاه شما از شما اثر انگشت، چهره یا قفل صفحه را درخواست کند.',
      title: 'از کلید عبور خود استفاده کنید',
    },
    password: {
      actionLink: 'استفاده از روش دیگر',
      subtitle: 'برای ادامه، رمز عبور فعلی خود را وارد کنید',
      title: 'تایید هویت الزامی است',
    },
    phoneCode: {
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه، کد ارسال شده به تلفن خود را وارد کنید',
      title: 'تایید هویت الزامی است',
    },
    phoneCodeMfa: {
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه، کد ارسال شده به تلفن خود را وارد کنید',
      title: 'تایید هویت الزامی است',
    },
    totpMfa: {
      formTitle: 'کد تأیید',
      subtitle: 'برای ادامه، کد تولید شده توسط برنامه تأیید هویت خود را وارد کنید',
      title: 'تایید هویت الزامی است',
    },
  },
  searchInput: {
    action__clear: undefined,
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'افزودن حساب',
      action__signOutAll: 'خروج از همه حساب‌ها',
      subtitle: 'حسابی را که می‌خواهید با آن ادامه دهید انتخاب کنید.',
      title: 'یک حساب انتخاب کنید',
    },
    alternativeMethods: {
      actionLink: 'کمک بگیرید',
      actionText: 'هیچ کدام از اینها را ندارید؟',
      blockButton__backupCode: 'از کد پشتیبان استفاده کنید',
      blockButton__emailCode: 'کد را به {{identifier}} ایمیل کنید',
      blockButton__emailLink: 'لینک ایمیل به {{identifier}}',
      blockButton__passkey: 'با کلید عبور خود وارد شوید',
      blockButton__password: 'با رمز عبور خود وارد شوید',
      blockButton__phoneCode: 'ارسال کد پیامکی به {{identifier}}',
      blockButton__totp: 'از برنامه تأیید هویت خود استفاده کنید',
      getHelp: {
        blockButton__emailSupport: 'پشتیبانی ایمیل',
        content:
          'اگر در تأیید حساب کاربری خود مشکل دارید، به ما ایمیل بزنید و ما در اسرع وقت به شما کمک میکنیم تا دسترسی دوباره داشته باشید.',
        title: 'کمک بگیرید',
      },
      subtitle: 'با مشکل مواجه شدید؟ می‌توانید از هر یک از این روش‌ها برای ورود به سیستم استفاده کنید.',
      title: 'استفاده از روش دیگر',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه به {{applicationName}}',
      title: '{{provider}} خود را بررسی کنید',
    },
    backupCodeMfa: {
      subtitle: 'کد پشتیبان شما همان کدی است که هنگام تنظیم احراز هویت دو مرحله‌ای دریافت کرده‌اید.',
      title: 'یک کد پشتیبان وارد کنید',
    },
    emailCode: {
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه به {{applicationName}}',
      title: 'ایمیل خود را بررسی کنید',
    },
    emailCodeMfa: {
      formTitle: 'ایمیل خود را بررسی کنید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه به {{applicationName}}',
      title: 'ایمیل خود را بررسی کنید',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'برای ادامه، لینک تأیید را در دستگاه و مرورگری که از طریق آن ورود به سیستم را آغاز کرده‌اید، باز کنید.',
        title: 'لینک تأیید برای این دستگاه نامعتبر است',
      },
      expired: {
        subtitle: 'برای ادامه به برگه اصلی برگردید.',
        title: 'این لینک تأیید منقضی شده است',
      },
      failed: {
        subtitle: 'برای ادامه به برگه اصلی برگردید.',
        title: 'ین لینک تایید نامعتبر است',
      },
      formSubtitle: 'از لینک تایید ارسال شده به ایمیلتان استفاده کنید',
      formTitle: 'لینک تأیید',
      loading: {
        subtitle: 'به زودی منتقل خواهید شد',
        title: 'در حال ورود...',
      },
      resendButton: 'لینکی دریافت نکردید؟ دوباره ارسال کنید',
      subtitle: 'برای ادامه به {{applicationName}}',
      title: 'ایمیل خود را بررسی کنید',
      unusedTab: {
        title: 'می‌توانید این برگه را ببندید',
      },
      verified: {
        subtitle: 'به زودی منتقل خواهید شد',
        title: 'با موفقیت وارد شدید',
      },
      verifiedSwitchTab: {
        subtitle: 'برای ادامه به برگه اصلی برگردید',
        subtitleNewTab: 'برای ادامه به برگه تازه باز شده برگردید',
        titleNewTab: 'در برگه دیگر وارد سیستم شده‌اید',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'از لینک تأیید ارسال شده به ایمیل خود استفاده کنید',
      resendButton: 'لینک را دریافت نکردید؟ ارسال مجدد',
      subtitle: 'برای ادامه به {{applicationName}}',
      title: 'ایمیل خود را بررسی کنید',
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'کد رمز عبور را بازنشانی کنید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای بازنشانی رمز عبور خود',
      subtitle_email: 'ابتدا کد ارسال شده به ایمیل خود را وارد کنید',
      subtitle_phone: 'ابتدا کد ارسال شده به تلفن همراه خود را وارد کنید',
      title: 'بازنشانی رمز عبور',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'رمز عبور خود را بازنشانی کنید',
      label__alternativeMethods: 'یا با روش دیگری وارد شوید',
      title: 'رمز عبور را فراموش کرده‌اید؟',
    },
    newDeviceVerificationNotice:
      'شما از یک دستگاه جدید وارد می‌شوید. برای حفظ امنیت حساب کاربری شما، درخواست تأیید می‌کنیم.',
    noAvailableMethods: {
      message: 'نمی‌توان وارد سیستم شد. هیچ عامل احراز هویتی در دسترس نیست.',
      subtitle: 'خطایی رخ داد',
      title: 'نمی توان وارد سیستم شد',
    },
    passkey: {
      subtitle:
        'ستفاده از کلید عبور، هویت شما را تأیید می‌کند. ممکن است دستگاه از شما اثر انگشت، چهره یا قفل صفحه را درخواست کند.',
      title: 'از کلید عبور خود استفاده کنید',
    },
    password: {
      actionLink: 'استفاده از روش دیگر',
      subtitle: 'رمز عبور مرتبط با حساب کاربری خود را وارد کنید',
      title: 'رمز عبور خود را وارد کنید',
    },
    passwordCompromised: {
      title: undefined,
    },
    passwordPwned: {
      title: 'رمز عبور به خطر افتاده است',
    },
    passwordUntrusted: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه به {{applicationName}}',
      title: 'تلفن همراه خود را چک کنید',
    },
    phoneCodeMfa: {
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'برای ادامه، لطفاً کد تأیید ارسال شده به تلفن خود را وارد کنید',
      title: 'تلفن همراه خود را چک کنید',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    resetPassword: {
      formButtonPrimary: 'بازنشانی رمز عبور',
      requiredMessage: 'به دلایل امنیتی، لازم است رمز عبور خود را مجدداً تنظیم کنید.',
      successMessage: 'رمز عبور شما با موفقیت تغییر یافت. در حال ورود به سیستم هستید، لطفاً لحظه‌ای صبر کنید.',
      title: 'رمز عبور جدید تنظیم کنید',
    },
    resetPasswordMfa: {
      detailsLabel: 'قبل از تنظیم مجدد رمز عبور، باید هویت شما را تأیید کنیم.',
    },
    start: {
      actionLink: 'ثبت نام',
      actionLink__join_waitlist: 'به لیست انتظار بپیوندید',
      actionLink__use_email: 'استفاده از ایمیل',
      actionLink__use_email_username: 'از ایمیل یا نام کاربری استفاده کنید',
      actionLink__use_passkey: 'به جای آن از کلید عبور استفاده کنید',
      actionLink__use_phone: 'از تلفن همراه استفاده کنید',
      actionLink__use_username: 'از نام کاربری استفاده کنید',
      actionText: 'حساب کاربری ندارید؟',
      actionText__join_waitlist: 'دسترسی زودهنگام می خواهید؟',
      alternativePhoneCodeProvider: {
        actionLink: 'استفاده از روش دیگر',
        label: ' شماره تلفن {{provider}}',
        subtitle: 'شماره تلفن خود را وارد کنید تا کد تأیید را در {{provider}} دریافت کنید.',
        title: 'با {{provider}} وارد {{applicationName}} شوید',
      },
      subtitle: 'خوش آمدید! لطفا برای ادامه وارد شوید',
      subtitleCombined: 'برای ادامه به {{applicationName}}، جزئیات خود را وارد کنید',
      title: 'وارد {{applicationName}} شوید',
      titleCombined: 'ادامه به {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'کد تأیید',
      subtitle: 'برای ادامه، لطفاً کد تأیید تولید شده توسط برنامه تأیید هویت خود را وارد کنید',
      title: 'تأیید  دو مرحله‌ای',
    },
    web3Solana: {
      subtitle: 'برای ورود، یک کیف پول را در زیر انتخاب کنید',
      title: 'ورود با Solana',
    },
  },
  signInEnterPasswordTitle: 'رمز عبور خود را وارد کنید',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'کد تأیید ارسال شده به {{provider}} خود را وارد کنید',
      title: '{{provider}} خود را تأیید کنید',
    },
    continue: {
      actionLink: 'ورود',
      actionText: 'حساب کاربری دارید؟',
      subtitle: 'لطفاً برای ادامه، جزئیات باقی‌مانده را وارد کنید.',
      title: 'فیلدهای خالی را پر کنید',
    },
    emailCode: {
      formSubtitle: 'کد تایید ارسال شده به آدرس ایمیل خود را وارد کنید',
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'کد تایید ارسال شده به ایمیلتان را وارد کنید',
      title: 'ایمیل خود را تأیید کنید',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'برای ادامه، لینک تأیید را در دستگاه و مرورگری که ثبت نام را از طریق آن آغاز کرده‌اید، باز کنید.',
        title: 'لینک تأیید برای این دستگاه نامعتبر است',
      },
      formSubtitle: 'از لینک تایید ارسال شده به ایمیلتان استفاده کنید',
      formTitle: 'لینک تأیید',
      loading: {
        title: 'در حال ثبت نام...',
      },
      resendButton: 'لینکی دریافت نکردید؟ دوباره ارسال کنید',
      subtitle: 'برای ادامه به {{applicationName}}',
      title: 'ایمیل خود را تأیید کنید',
      verified: {
        title: 'با موفقیت ثبت نام شدید',
      },
      verifiedSwitchTab: {
        subtitle: 'برای ادامه به برگه تازه باز شده برگردید',
        subtitleNewTab: 'برای ادامه به برگه قبلی برگردید',
        title: 'ایمیل با موفقیت تأیید شد',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'من با {{ privacyPolicyLink || link("Privacy Policy") }} موافقم.',
        label__onlyTermsOfService: 'من با {{ termsOfServiceLink || link("Terms of Service") }} موافقم.',
        label__termsOfServiceAndPrivacyPolicy:
          'من با {{ termsOfServiceLink || link("Terms of Service") }} و {{ privacyPolicyLink || link("Privacy Policy") }} موافقم.',
      },
      continue: {
        subtitle: 'لطفا شرایط را برای ادامه بخوانید و بپذیرید',
        title: 'رضایت قانونی',
      },
    },
    phoneCode: {
      formSubtitle: 'کد تایید ارسال شده به شماره تلفن خود را وارد کنید',
      formTitle: 'کد تأیید',
      resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
      subtitle: 'کد تایید ارسال شده به تلفن خود را وارد کنید',
      title: 'تلفن همراه خود را تایید کنید',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    restrictedAccess: {
      actionLink: 'ورود',
      actionText: 'حساب کاربری دارید؟',
      blockButton__emailSupport: 'پشتیبانی ایمیل',
      blockButton__joinWaitlist: 'به لیست انتظار بپیوندید',
      subtitle:
        'ثبت نام در حال حاضر غیرفعال است. اگر فکر می‌کنید باید دسترسی داشته باشید، لطفاً با پشتیبانی تماس بگیرید.',
      subtitleWaitlist:
        'ثبت نام در حال حاضر غیرفعال است. برای اینکه اولین نفری باشید که از زمان شروع به کار ما مطلع می‌شود، به لیست انتظار بپیوندید.',
      title: 'دسترسی محدود شده است',
    },
    start: {
      actionLink: 'ورود',
      actionLink__use_email: 'به جای آن از ایمیل استفاده کنید',
      actionLink__use_phone: 'به جای آن از تلفن استفاده کنید',
      actionText: 'حساب کاربری دارید؟',
      alternativePhoneCodeProvider: {
        actionLink: 'استفاده از روش دیگر',
        label: 'شماره تلفن {{provider}}',
        subtitle: 'شماره تلفن خود را وارد کنید تا کد تأیید را در {{provider}} دریافت کنید.',
        title: 'ثبت نام در {{applicationName}} با {{provider}}',
      },
      subtitle: 'خوش آمدید! لطفا برای شروع، اطلاعات را پر کنید.',
      subtitleCombined: 'خوش آمدید! لطفا برای شروع، اطلاعات را پر کنید.',
      title: 'حساب کاربری خود را ایجاد کنید',
      titleCombined: 'حساب کاربری خود را ایجاد کنید',
    },
    web3Solana: {
      subtitle: 'برای ثبت‌نام، یک کیف پول را در زیر انتخاب کنید',
      title: 'ثبت‌نام با Solana',
    },
  },
  socialButtonsBlockButton: 'ادامه با {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'سازمانی برای نام شرکت شناسایی شده ({{organizationName}}) و {{organizationDomain}} از قبل وجود دارد. از طریق دعوتنامه بپیوندید.',
    },
    chooseOrganization: {
      action__createOrganization: 'ایجاد سازمان',
      action__invitationAccept: 'بپیوندید',
      action__suggestionsAccept: 'درخواست عضویت',
      subtitle: 'برای ادامه، سازمانی را انتخاب کنید',
      subtitle__createOrganizationDisabled: 'به یک سازمان موجود بپیوندید',
      suggestionsAcceptedLabel: 'در انتظار تایید',
      title: 'یک سازمان انتخاب کنید',
    },
    createOrganization: {
      formButtonReset: 'گذشتن',
      formButtonSubmit: 'ایجاد سازمان',
      formFieldInputPlaceholder__name: undefined,
      formFieldInputPlaceholder__slug: undefined,
      formFieldLabel__name: undefined,
      formFieldLabel__slug: undefined,
      subtitle: 'سازمان جدیدی برای شروع کار ایجاد کنید',
      title: 'ایجاد سازمان جدید',
    },
    organizationCreationDisabled: {
      subtitle: 'برای دریافت دعوتنامه با مدیر سازمان خود تماس بگیرید.',
      title: 'شما باید عضو یک سازمان باشید',
    },
    signOut: {
      actionLink: 'خروج از همه حساب‌ها',
      actionText: 'می‌خواهید خارج شوید؟',
    },
  },
  taskResetPassword: {
    formButtonPrimary: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    subtitle: undefined,
    title: undefined,
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    action_blocked: undefined,
    already_a_member_in_organization: '{{email}} از قبل عضو سازمان است.',
    api_key_name_already_exists: undefined,
    api_key_usage_exceeded: undefined,
    avatar_file_size_exceeded: 'حجم فایل از حداکثر مجاز ۱۰ مگابایت بیشتر است. لطفاً فایل کوچکتری انتخاب کنید.',
    avatar_file_type_invalid: 'نوع فایل پشتیبانی نمی‌شود. لطفاً تصویری با فرمت JPG، PNG، GIF یا WEBP آپلود کنید.',
    captcha_invalid: 'کپچا نامعتبر است. لطفاً دوباره امتحان کنید.',
    captcha_unavailable:
      'به دلیل عدم موفقیت در اعتبارسنجی ربات، ثبت نام ناموفق بود. لطفاً برای امتحان مجدد، صفحه را رفرش کنید یا برای راهنمایی بیشتر با پشتیبانی تماس بگیرید.',
    form_code_incorrect: 'کد وارد شده نادرست است.',
    form_email_address_blocked:
      'سرویس‌های ایمیل موقت پشتیبانی نمی‌شوند. لطفاً از آدرس ایمیل معمولی خود برای ایجاد حساب استفاده کنید.',
    form_identifier_exists__email_address: 'این آدرس ایمیل قبلاً استفاده شده است.',
    form_identifier_exists__phone_number: 'این شماره تلفن قبلاً استفاده شده است.',
    form_identifier_exists__username: 'این نام کاربری قبلاً گرفته شده است.',
    form_identifier_not_found: 'شناسه یافت نشد.',
    form_new_password_matches_current: undefined,
    form_param_format_invalid: 'فرمت پارامتر نامعتبر است.',
    form_param_format_invalid__email_address: 'آدرس ایمیل نامعتبر است.',
    form_param_format_invalid__phone_number: 'شماره تلفن نامعتبر است.',
    form_param_max_length_exceeded__first_name: 'نام نباید بیشتر از حد مجاز طولانی باشد.',
    form_param_max_length_exceeded__last_name: 'نام خانوادگی نباید بیشتر از حد مجاز طولانی باشد.',
    form_param_max_length_exceeded__name: 'نام نباید بیشتر از حد مجاز طولانی باشد.',
    form_param_nil: 'پارامتر مورد نیاز است.',
    form_param_type_invalid: 'نوع پارامتر نامعتبر است.',
    form_param_type_invalid__email_address: 'آدرس ایمیل باید یک رشته معتبر باشد.',
    form_param_type_invalid__phone_number: 'شماره تلفن باید یک رشته معتبر باشد.',
    form_param_value_invalid: 'مقدار پارامتر نامعتبر است.',
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: 'رمز عبور نادرست است.',
    form_password_length_too_short: 'رمز عبور شما خیلی کوتاه است. باید حداقل ۸ کاراکتر داشته باشد.',
    form_password_not_strong_enough: 'رمز عبور شما به اندازه کافی قوی نیست.',
    form_password_or_identifier_incorrect:
      'رمز عبور یا آدرس ایمیل نادرست است. دوباره تلاش کنید یا از روش دیگری استفاده کنید.',
    form_password_pwned:
      'این رمز عبور به عنوان بخشی از یک نقض امنیتی یافت شده و قابل استفاده نیست، لطفاً رمز عبور دیگری را امتحان کنید.',
    form_password_pwned__sign_in:
      'این رمز عبور به عنوان بخشی از یک نقض امنیتی یافت شده و قابل استفاده نیست، لطفاً رمز عبور خود را مجدداً تنظیم کنید.',
    form_password_size_in_bytes_exceeded: 'رمز عبور خیلی طولانی است.',
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: 'اعتبارسنجی رمز عبور ناموفق بود.',
    form_username_invalid_character: 'نام کاربری شامل کاراکترهای نامعتبر است.',
    form_username_invalid_length: 'نام کاربری شما باید بین {{min_length}} و {{max_length}} کاراکتر باشد.',
    form_username_needs_non_number_char: 'نام کاربری شما باید حداقل شامل یک کاراکتر غیر عددی باشد.',
    identification_deletion_failed: 'حذف شناسه ناموفق بود.',
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access: 'دسترسی مجاز نیست.',
    oauth_access_denied: undefined,
    organization_domain_blocked: 'دامنه سازمان مسدود شده است.',
    organization_domain_common: 'دامنه سازمان عمومی است و قابل قبول نیست.',
    organization_domain_exists_for_enterprise_connection: 'این دامنه برای اتصال سازمانی موجود است.',
    organization_membership_quota_exceeded: 'حد مجاز عضویت سازمان تجاوز شده است.',
    organization_minimum_permissions_needed: 'حداقل مجوزهای مورد نیاز برای سازمان الزامی است.',
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: 'یک کلید عبور از قبل در این دستگاه ثبت شده است.',
    passkey_not_supported: 'کلیدهای عبور در این دستگاه پشتیبانی نمی‌شوند.',
    passkey_pa_not_supported: 'ثبت نام نیاز به یک احراز هویت کننده پلتفرم دارد اما دستگاه از آن پشتیبانی نمی‌کند.',
    passkey_registration_cancelled: 'ثبت کلید عبور لغو یا مهلت آن به پایان رسیده است.',
    passkey_retrieval_cancelled: 'تأیید کلید عبور لغو یا مهلت آن تمام شده است.',
    passwordComplexity: {
      maximumLength: 'کمتر از {{length}} کاراکتر',
      minimumLength: '{{length}} یا کاراکترهای بیشتر',
      requireLowercase: 'یک حرف کوچک',
      requireNumbers: 'یک شماره',
      requireSpecialCharacter: 'یک نشانه خاص',
      requireUppercase: 'یک حرف بزرگ',
      sentencePrefix: 'رمز عبور شما باید حاوی',
    },
    phone_number_exists: 'این شماره تلفن قبلاً استفاده شده است.',
    protect_check_aborted: undefined,
    protect_check_already_resolved: undefined,
    protect_check_execution_failed: undefined,
    protect_check_invalid_script: undefined,
    protect_check_invalid_sdk_url: undefined,
    protect_check_script_load_failed: undefined,
    protect_check_timed_out: undefined,
    protect_check_unsupported_environment: undefined,
    session_exists: 'جلسه از قبل وجود دارد.',
    web3_missing_identifier: 'افزونه‌ی کیف پول وب۳ پیدا نشد. برای ادامه، لطفاً یکی نصب کنید.',
    web3_signature_request_rejected: 'درخواست امضا را رد کرده‌اید. برای ادامه دوباره تلاش کنید.',
    web3_solana_signature_generation_failed: 'هنگام ایجاد امضا خطایی رخ داد. برای ادامه دوباره تلاش کنید.',
    zxcvbn: {
      couldBeStronger: 'رمز عبور شما کار می‌کند، اما می‌توانست قوی‌تر باشد. سعی کنید کاراکترهای بیشتری اضافه کنید.',
      goodPassword: 'رمز عبور شما تمام شرایط لازم را برآورده می‌کند.',
      notEnough: 'رمز عبور شما به اندازه کافی قوی نیست.',
      suggestions: {
        allUppercase: 'بعضی از حروف را بزرگ بنویسید، اما نه همه آنها را.',
        anotherWord: 'کلمات بیشتری اضافه کنید که کمتر رایج هستند.',
        associatedYears: 'از سال‌هایی که با شما مرتبط هستند، دوری کنید.',
        capitalization: 'بیشتر از حرف اول با حروف بزرگ بنویسید.',
        dates: 'از تاریخ‌ها و سال‌های مرتبط با خودتان اجتناب کنید.',
        l33t: "از جایگزینی حروف قابل پیش‌بینی مانند '@' به جای 'a' خودداری کنید.",
        longerKeyboardPattern: 'از الگوهای طولانی‌تر کیبورد استفاده کنید و جهت تایپ را چندین بار تغییر دهید.',
        noNeed: 'شما می‌توانید بدون استفاده از نمادها، اعداد یا حروف بزرگ، رمزهای عبور قوی ایجاد کنید.',
        pwned: 'اگر از این رمز عبور در جای دیگری استفاده می‌کنید، باید آن را تغییر دهید.',
        recentYears: 'از سال های اخیر اجتناب کنید.',
        repeated: 'از تکرار کلمات و کاراکترها خودداری کنید.',
        reverseWords: 'از املای معکوس کلمات رایج خودداری کنید.',
        sequences: 'از توالی‌های کاراکتری رایج اجتناب کنید.',
        useWords: 'از کلمات متعدد استفاده کنید، اما از عبارات رایج پرهیز کنید',
      },
      warnings: {
        common: 'این یک رمز عبور رایج است.',
        commonNames: 'نام‌ها و نام‌های خانوادگی رایج به راحتی قابل حدس زدن هستند.',
        dates: 'حدس زدن تاریخ‌ها آسان است.',
        extendedRepeat: 'الگوهای کاراکتری تکراری مانند "abcabcabc" به راحتی قابل حدس زدن هستند.',
        keyPattern: 'الگوهای کوتاه صفحه کلید به راحتی قابل حدس زدن هستند.',
        namesByThemselves: 'حدس زدن نام‌ها یا نام‌های خانوادگی مجرد آسان است.',
        pwned: 'رمز عبور شما در اثر یک نقض امنیتی در اینترنت فاش شده است.',
        recentYears: 'سال‌های اخیر را به راحتی می‌توان حدس زد.',
        sequences: 'حدس زدن توالی‌های کاراکتری رایج مانند "abc" آسان است.',
        similarToCommon: 'این شبیه به یک رمز عبور رایج است.',
        simpleRepeat: 'حدس زدن کاراکترهای تکراری مانند "aaa" آسان است.',
        straightRow: 'ردیف‌های صاف کلیدها روی صفحه کلید شما به راحتی قابل حدس زدن هستند.',
        topHundred: 'این یک رمز عبور رایج است.',
        topTen: 'این یک رمز عبور پرکاربرد است.',
        userInputs: 'نباید هیچ اطلاعات شخصی یا مربوط به صفحه وجود داشته باشد.',
        wordByItself: 'کلمات تکی به راحتی قابل حدس زدن هستند.',
      },
    },
  },
  userButton: {
    action__addAccount: 'افزودن حساب',
    action__closeUserMenu: undefined,
    action__manageAccount: 'مدیریت حساب',
    action__openUserMenu: undefined,
    action__signOut: 'خروج',
    action__signOutAll: 'خروج از همه حساب‌ها',
    label__accountActions: 'اقدامات حساب',
    label__activeSessions: 'جلسات فعال',
    label__userButtonPopover: 'پنل حساب',
  },
  userProfile: {
    apiKeysPage: {
      title: 'کلیدهای API',
    },
    backupCodePage: {
      actionLabel__copied: 'کپی شده!',
      actionLabel__copy: 'کپی همه',
      actionLabel__download: 'Download .txt',
      actionLabel__print: 'چاپ',
      infoText1: 'کدهای پشتیبان برای این حساب فعال خواهند شد.',
      infoText2:
        'کدهای پشتیبان را مخفی نگه دارید و آنها را در جای امنی ذخیره کنید. در صورت مشکوک بودن به لو رفتن کدهای پشتیبان، می‌توانید آنها را دوباره ایجاد کنید.',
      subtitle__codelist: 'آنها را به طور ایمن ذخیره کنید و آنها را مخفی نگه دارید.',
      successMessage:
        'کدهای پشتیبان اکنون فعال شده‌اند. در صورت از دست دادن دسترسی به دستگاه احراز هویت خود، می‌توانید از یکی از این کدها برای ورود به حساب کاربری خود استفاده کنید. هر کد فقط یک بار قابل استفاده است.',
      successSubtitle:
        'اگر دسترسی به دستگاه احراز هویت خود را از دست دادید، می‌توانید از یکی از این موارد برای ورود به حساب کاربری خود استفاده کنید.',
      title: 'افزودن  کد پشتیبان',
      title__codelist: 'کدهای پشتیبان',
    },
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'تاریخچه پرداخت خالی است',
        notFound: 'هیچ پرداختی یافت نشد',
        tableHeader__amount: 'مبلغ',
        tableHeader__date: 'تاریخ',
        tableHeader__status: 'وضعیت',
      },
      paymentMethodsSection: {
        actionLabel__default: 'پیش فرض کنید',
        actionLabel__remove: 'حذف',
        add: 'افزودن روش پرداخت جدید',
        addSubtitle: 'یک روش پرداخت جدید به حساب خود اضافه کنید.',
        cancelButton: 'لغو',
        formButtonPrimary__add: 'افزودن روش پرداخت',
        formButtonPrimary__pay: '{{amount}} پرداخت',
        payWithTestCardButton: 'پرداخت با کارت تست',
        removeMethod: {
          messageLine1: '{{identifier}} از این حساب حذف خواهد شد.',
          messageLine2:
            'شما دیگر نمی‌توانید از این منبع پرداخت استفاده کنید و هرگونه اشتراک دوره‌ای وابسته به آن دیگر کار نخواهد کرد.',
          successMessage: '{{paymentMethod}} از حساب شما حذف شده است.',
          title: 'حذف روش پرداخت',
        },
        title: 'روش های پرداخت',
      },
      start: {
        headerTitle__payments: 'پرداخت‌ها',
        headerTitle__plans: 'طرح ها',
        headerTitle__statements: 'بیانیه‌ها',
        headerTitle__subscriptions: 'اشتراک',
      },
      statementsSection: {
        empty: 'بیانیه‌ها خالی است',
        itemCaption__paidForPlan: 'پرداخت شده برای طرح {{plan}}',
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: 'اعتبار تناسبی',
        itemCaption__subscribedAndPaidForPlan: 'مشترک و پرداخت شده برای طرح {{plan}}',
        notFound: 'هیچ بیانیه‌ای یافت نشد',
        tableHeader__amount: 'مبلغ',
        tableHeader__date: 'تاریخ',
        title: 'بیانیه‌ها',
        totalPaid: 'کل پرداختی',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: 'در یک طرح مشترک شوید',
        actionLabel__switchPlan: 'تغییر طرح',
        overview: undefined,
        tableHeader__edit: 'ویرایش',
        tableHeader__plan: 'طرح',
        tableHeader__startDate: 'تاریخ شروع',
        title: 'اشتراک',
      },
      subscriptionsSection: {
        actionLabel__default: 'مدیریت',
      },
      switchPlansSection: {
        title: 'تغییر طرح',
      },
      title: 'صورتحساب',
    },
    connectedAccountPage: {
      formHint: 'یک ارائه دهنده برای اتصال حساب خود انتخاب کنید.',
      formHint__noAccounts: 'هیچ ارائه دهنده حساب خارجی در دسترس نیست.',
      removeResource: {
        messageLine1: '{{identifier}} از این حساب حذف خواهد شد.',
        messageLine2:
          'دیگر نمی‌توانید از این حساب متصل استفاده کنید و هیچ یک از ویژگی‌های وابسته دیگر کار نخواهند کرد.',
        successMessage: '{{connectedAccount}} از حساب شما حذف شده است.',
        title: 'حساب متصل را حذف کنید',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'ارائه دهنده به حساب شما اضافه شده است',
      title: 'اضافه کردن حساب متصل',
    },
    deletePage: {
      actionDescription: 'برای ادامه، عبارت "Delete account" را در زیر تایپ کنید.',
      confirm: 'حذف حساب',
      messageLine1:
        'آیا مطمئن هستید که می‌خواهید حساب خود را حذف کنید؟ برخی از داده‌های مرتبط ممکن است حفظ شوند. برای درخواست حذف کامل داده‌ها، لطفاً با پشتیبانی تماس بگیرید.',
      messageLine2: 'این اقدام دائمی و غیرقابل برگشت است.',
      title: 'حذف حساب',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'ایمیلی حاوی کد تایید به این آدرس ایمیل ارسال خواهد شد.',
        formSubtitle: 'کد تایید ارسال شده به {{identifier}} را وارد کنید',
        formTitle: 'کد تأیید',
        resendButton: 'کدی دریافت نکردید؟ ارسال دوباره',
        successMessage: 'ایمیل {{identifier}} به حساب شما اضافه شد.',
      },
      emailLink: {
        formHint: 'ایمیلی حاوی لینک تأیید به این آدرس ایمیل ارسال خواهد شد.',
        formSubtitle: 'روی لینک تأیید در ایمیل ارسال شده به {{identifier}} کلیک کنید',
        formTitle: 'لینک تأیید',
        resendButton: 'لینکی دریافت نکردید؟ دوباره ارسال کنید',
        successMessage: 'ایمیل {{identifier}} به حساب شما اضافه شد.',
      },
      enterpriseSSOLink: {
        formButton: 'برای ورود کلیک کنید',
        formSubtitle: 'ورود به سیستم را با {{identifier}} تکمیل کنید',
      },
      formHint: 'قبل از اینکه بتوانید این آدرس ایمیل را به حساب خود اضافه کنید، باید آن را تأیید کنید.',
      removeResource: {
        messageLine1: '{{identifier}} از این حساب حذف خواهد شد.',
        messageLine2: 'دیگر نمی‌توانید با استفاده از این آدرس ایمیل وارد سیستم شوید.',
        successMessage: '{{emailAddress}} از حساب شما حذف شده است.',
        title: 'آدرس ایمیل را حذف کنید',
      },
      title: 'آدرس ایمیل را اضافه کنید',
      verifyTitle: 'آدرس ایمیل را تأیید کنید',
    },
    formButtonPrimary__add: 'افزودن',
    formButtonPrimary__continue: 'ادامه',
    formButtonPrimary__finish: 'اتمام',
    formButtonPrimary__remove: 'حذف',
    formButtonPrimary__save: 'ذخیره',
    formButtonReset: 'لغو',
    mfaPage: {
      formHint: 'یک روش برای اضافه کردن انتخاب کنید',
      title: 'اضافه کردن تایید هویت دو مرحله‌ای',
    },
    mfaPhoneCodePage: {
      backButton: 'از شماره موجود استفاده کنید',
      primaryButton__addPhoneNumber: 'شماره تلفن را اضافه کنید',
      removeResource: {
        messageLine1: '{{identifier}} دیگر هنگام ورود به سیستم، کدهای تأیید را دریافت نخواهد کرد.',
        messageLine2: 'ممکن است حساب شما به اندازه کافی امن نباشد. آیا مطمئن هستید که می‌خواهید ادامه دهید؟',
        successMessage: 'تأیید دو مرحله‌ای کد پیامکی برای {{mfaPhoneCode}} حذف شده است.',
        title: 'حذف تایید دو مرحله‌ای',
      },
      subtitle__availablePhoneNumbers:
        'رای ثبت نام در تأیید دو مرحله‌ای با کد پیامکی، یک شماره تلفن موجود را انتخاب کنید یا یک شماره جدید اضافه کنید.',
      subtitle__unavailablePhoneNumbers:
        'هیچ شماره تلفنی برای ثبت نام جهت دریافت کد تایید دو مرحله‌ای از طریق پیامک وجود ندارد، لطفاً شماره جدیدی اضافه کنید.',
      successMessage1:
        'هنگام ورود به سیستم، باید کد تأیید ارسال شده به این شماره تلفن را به عنوان یک مرحله اضافی وارد کنید.',
      successMessage2:
        'این کدهای پشتیبان را ذخیره کرده و در جایی امن نگهداری کنید. اگر دسترسی به دستگاه احراز هویت خود را از دست دادید، می‌توانید از کدهای پشتیبان برای ورود به سیستم استفاده کنید.',
      successTitle: ' کد تأیید پیامکی فعال شد',
      title: 'افزودن کد تأیید پیامکی',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'در عوض کد QR را اسکن کنید',
        buttonUnableToScan__nonPrimary: 'نمی‌توانید کد QR را اسکن کنید؟',
        infoText__ableToScan:
          'یک روش ورود جدید در برنامه تأیید هویت خود تنظیم کنید و کد QR زیر را اسکن کنید تا آن را به حساب خود پیوند دهید.',
        infoText__unableToScan: 'یک روش ورود جدید در تأییدکننده خود تنظیم کنید و کلید ارائه شده در زیر را وارد کنید.',
        inputLabel__unableToScan1:
          'مطمئن شوید که رمزهای عبور مبتنی بر زمان یا یکبار مصرف فعال هستند، سپس اتصال حساب خود را تکمیل کنید.',
        inputLabel__unableToScan2:
          'از طرف دیگر، اگر تأییدکننده‌ی هویت شما از TOTP URI پشتیبانی می‌کند، می‌توانید URI کامل را نیز کپی کنید.',
      },
      removeResource: {
        messageLine1: 'کدهای تأیید از این تأییدکننده هویت دیگر هنگام ورود به سیستم لازم نخواهند بود.',
        messageLine2: 'ممکن است حساب شما به اندازه کافی امن نباشد. آیا مطمئن هستید که می‌خواهید ادامه دهید؟',
        successMessage: 'تأیید دو مرحله‌ای از طریق برنامه تأیید هویت حذف شده است.',
        title: 'حذف تایید دو مرحله‌ای',
      },
      successMessage:
        'تأیید هویت دو مرحله‌ای اکنون فعال شده است. هنگام ورود به سیستم، باید یک کد تأیید از این تأییدکننده را به عنوان یک مرحله اضافی وارد کنید.',
      title: 'افزودن برنامه احراز هویت',
      verifySubtitle: 'کد تأیید تولید شده توسط برنامه احراز هویت خود را وارد کنید',
      verifyTitle: 'کد تأیید',
    },
    mobileButton__menu: 'منو',
    navbar: {
      account: 'نمایه',
      apiKeys: 'کلیدهای API',
      billing: 'صورتحساب',
      description: 'اطلاعات حساب خود را مدیریت کنید.',
      security: 'امنیت',
      title: 'حساب',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} از این حساب حذف خواهد شد.',
        title: 'حذف کلید عبور',
      },
      subtitle__rename: 'می‌توانید نام کلید عبور را تغییر دهید تا پیدا کردن آن آسان‌تر شود.',
      title__rename: 'تغییر نام کلید عبور',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'توصیه می‌شود از تمام دستگاه‌های دیگری که ممکن است از رمز عبور قدیمی شما استفاده کرده باشند، خارج شوید.',
      readonly: 'در حال حاضر رمز عبور شما قابل ویرایش نیست زیرا فقط از طریق اتصال سازمانی می‌توانید وارد سیستم شوید.',
      successMessage__set: 'رمز عبور شما تنظیم شد.',
      successMessage__signOutOfOtherSessions: 'از همه دستگاه‌های دیگر خارج شده است.',
      successMessage__update: 'رمز عبور شما به‌روزرسانی شد.',
      title__set: 'تنظیم رمز عبور',
      title__update: 'ه‌روزرسانی رمز عبور',
    },
    phoneNumberPage: {
      infoText: 'یک پیامک حاوی کد تأیید به این شماره تلفن ارسال خواهد شد. ممکن است هزینه پیامک و داده اعمال شود.',
      removeResource: {
        messageLine1: '{{identifier}} از این حساب حذف خواهد شد.',
        messageLine2: 'دیگر نمی‌توانید با استفاده از این شماره تلفن وارد سیستم شوید.',
        successMessage: '{{phoneNumber}} از حساب شما حذف شده است.',
        title: 'شماره تلفن را حذف کنید',
      },
      successMessage: '{{identifier}} به حساب شما اضافه شد.',
      title: 'شماره تلفن را اضافه کنید',
      verifySubtitle: 'کد تایید ارسال شده به {{identifier}} را وارد کنید',
      verifyTitle: 'شماره تلفن را تأیید کنید',
    },
    plansPage: {
      title: 'طرح ها',
    },
    profilePage: {
      fileDropAreaHint: 'اندازه توصیه شده ۱:۱، تا ۱۰ مگابایت.',
      imageFormDestructiveActionSubtitle: 'حذف',
      imageFormSubtitle: 'بارگذاری',
      imageFormTitle: 'تصویر نمایه',
      readonly: 'اطلاعات نمایه شما توسط اتصال سازمانی ارائه شده است و قابل ویرایش نیست.',
      successMessage: 'نمایه شما به‌روزرسانی شد.',
      title: 'به روز رسانی نمایه',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'از دستگاه خارج شوید',
        title: 'دستگاه های فعال',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'دوباره وصل شوید',
        actionLabel__reauthorize: 'اکنون مجوز دهید',
        destructiveActionTitle: 'حذف',
        primaryButton: 'اتصال حساب',
        subtitle__disconnected: 'این حساب کاربری قطع شده است.',
        subtitle__reauthorize:
          'محدوده‌های مورد نیاز به‌روزرسانی شده‌اند و ممکن است با محدودیت‌هایی در عملکرد مواجه شوید. لطفاً برای جلوگیری از هرگونه مشکل، این برنامه را مجدداً مجاز کنید.',
        title: 'حساب های متصل',
      },
      dangerSection: {
        deleteAccountButton: 'حذف حساب',
        title: 'حذف حساب',
      },
      emailAddressesSection: {
        destructiveAction: 'ایمیل را حذف کنید',
        detailsAction__nonPrimary: 'به عنوان اصلی تنظیم کنید',
        detailsAction__primary: 'تایید کامل',
        detailsAction__unverified: 'تأیید',
        primaryButton: 'آدرس ایمیل را اضافه کنید',
        title: 'آدرس های ایمیل',
      },
      enterpriseAccountsSection: {
        primaryButton: 'اتصال حساب',
        title: 'حساب های سازمانی',
      },
      headerTitle__account: 'جزئیات نمایه',
      headerTitle__security: 'امنیت',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'تولید دوباره',
          headerTitle: 'کدهای پشتیبان',
          subtitle__regenerate:
            'یک مجموعه جدید از کدهای پشتیبان امن دریافت کنید. کدهای پشتیبان قبلی حذف می‌شوند و قابل استفاده نخواهند بود.',
          title__regenerate: 'کدهای پشتیبان را دوباره تولید کنید',
        },
        phoneCode: {
          actionLabel__setDefault: 'به عنوان پیش فرض تنظیم کنید',
          destructiveActionLabel: 'حذف',
        },
        primaryButton: 'اضافه کردن تایید هویت دو مرحله‌ای',
        title: 'تأیید  دو مرحله‌ای',
        totp: {
          destructiveActionTitle: 'حذف',
          headerTitle: 'برنامه های دو مرحله‌ای',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'حذف',
        menuAction__rename: 'تغییرنام',
        primaryButton: 'یک کلید عبور اضافه کنید',
        title: 'کلیدهای عبور',
      },
      passwordSection: {
        primaryButton__setPassword: 'تنظیم رمز عبور',
        primaryButton__updatePassword: 'ه‌روزرسانی رمز عبور',
        title: 'رمز عبور',
      },
      phoneNumbersSection: {
        destructiveAction: 'شماره تلفن را حذف کنید',
        detailsAction__nonPrimary: 'به عنوان اصلی تنظیم کنید',
        detailsAction__primary: 'تایید کامل ',
        detailsAction__unverified: 'شماره تلفن را تأیید کنید',
        primaryButton: 'شماره تلفن را اضافه کنید',
        title: 'شماره های تلفن',
      },
      profileSection: {
        primaryButton: 'به روز رسانی نمایه',
        title: 'نمایه',
      },
      usernameSection: {
        primaryButton__setUsername: 'تنظیم نام کاربری',
        primaryButton__updateUsername: 'به‌روزرسانی نام کاربری',
        title: 'نام کاربری',
      },
      web3WalletsSection: {
        destructiveAction: 'حذف کیف پول',
        detailsAction__nonPrimary: 'به عنوان اصلی تنظیم کنید',
        primaryButton: 'اتصال کیف پول',
        title: 'کیف پول های Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'یک کیف پول Solana را برای اتصال به حساب خود انتخاب کنید.',
          title: 'افزودن کیف پول Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'نام کاربری شما به‌روزرسانی شد.',
      title__set: 'تنظیم نام کاربری',
      title__update: 'به‌روزرسانی نام کاربری',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} از این حساب حذف خواهد شد.',
        messageLine2: 'دیگر نمی‌توانید با استفاده از این کیف پول web3 وارد سیستم شوید.',
        successMessage: '{{web3Wallet}} از حساب شما حذف شده است.',
        title: 'کیف پول web3 را حذف کنید',
      },
      subtitle__availableWallets: 'یک کیف پول web3 برای اتصال به حساب خود انتخاب کنید.',
      subtitle__unavailableWallets: 'هیچ کیف پول وب۳ موجود نیست.',
      successMessage: 'کیف پول به حساب شما اضافه شد.',
      title: 'اضافه کردن کیف پول web3',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'ورود',
      actionText: 'از قبل دسترسی دارید؟',
      formButton: 'به لیست انتظار بپیوندید',
      subtitle: 'آدرس ایمیل خود را وارد کنید و ما به شما اطلاع خواهیم داد که چه زمانی مکان شما آماده است.',
      title: 'به لیست انتظار بپیوندید',
    },
    success: {
      message: 'به زودی منتقل خواهید شد...',
      subtitle: 'وقتی رزرو شما آماده شد، با شما تماس خواهیم گرفت',
      title: 'ممنون که به لیست انتظار پیوستید!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'اتصال با {{walletName}}',
    continue: 'ادامه با {{walletName}}',
    noneAvailable:
      'هیچ کیف پول Solana Web3 شناسایی نشد. لطفاً یک {{ solanaWalletsLink || link("wallet extension") }} پشتیبانی‌شده از Web3 نصب کنید.',
  },
} as const;
